/**
 * App Info - Display application information
 */

import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  IconButton,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useState } from "react";
import { useAppInfo } from "../../hooks/useAppInfo";

export default function AppInfo() {
  const { data: appInfo, isLoading, error } = useAppInfo();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load application info:{" "}
        {error instanceof Error ? error.message : "Unknown error"}
      </Alert>
    );
  }

  if (!appInfo) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        Warning! No application info available
      </Alert>
    );
  }

  const toggleExpanded = (key: string) => {
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const formatValue = (key: string, value: string | string[]) => {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <Chip label="Missing" color="warning" size="small" />;
      }
      return `${value.length} user${value.length === 1 ? "" : "s"}`;
    }

    switch (key) {
      case "environment":
        return (
          <Chip
            label={value}
            color={
              value === "production"
                ? "error"
                : value === "development"
                  ? "info"
                  : "default"
            }
            size="small"
          />
        );
      case "spineApiBaseUrl":
        return value === "missing" ? (
          <Chip label="Missing" color="warning" size="small" />
        ) : (
          <Typography component="span" sx={{ wordBreak: "break-all" }}>
            {value}
          </Typography>
        );
      case "buildTimestamp":
        return new Date(value).toLocaleString();
      default:
        return value === "missing" ? (
          <Chip label="Missing" color="warning" size="small" />
        ) : (
          value
        );
    }
  };

  const renderUserRow = (label: string, key: string, users: string[]) => [
    <TableRow key={key} hover>
      <TableCell component="th" scope="row" sx={{ fontWeight: "medium" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {label}
          {users.length > 0 && (
            <IconButton
              size="small"
              onClick={() => toggleExpanded(key)}
              sx={{ ml: 1 }}
            >
              {expandedRows[key] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>
      </TableCell>
      <TableCell>{formatValue(key, users)}</TableCell>
    </TableRow>,
    users.length > 0 && (
      <TableRow key={`${key}-details`}>
        <TableCell colSpan={2} sx={{ py: 0 }}>
          <Collapse in={expandedRows[key]}>
            <Box sx={{ py: 2, pl: 4 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email addresses:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {users.map((email, index) => (
                  <Typography
                    key={index}
                    component="li"
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                  >
                    {email}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    ),
  ];

  const rows = [
    { label: "Environment", key: "environment", value: appInfo.environment },
    { label: "AWS Region", key: "awsRegion", value: appInfo.awsRegion },
    {
      label: "SPINE API URL",
      key: "spineApiBaseUrl",
      value: appInfo.spineApiBaseUrl,
    },
    { label: "App Version", key: "version", value: appInfo.version },
    {
      label: "Build Timestamp",
      key: "buildTimestamp",
      value: appInfo.buildTimestamp,
    },
    {
      label: "Node.js Version",
      key: "nodeVersion",
      value: appInfo.nodeVersion,
    },
    { label: "Platform", key: "platform", value: appInfo.platform },
    {
      label: "Deployment Environment",
      key: "deploymentEnv",
      value: appInfo.deploymentEnv,
    },
    { label: "System Name", key: "systemName", value: appInfo.systemName },
  ];

  const allRows = [
    ...rows.map((row) => (
      <TableRow key={row.key} hover>
        <TableCell component="th" scope="row" sx={{ fontWeight: "medium" }}>
          {row.label}
        </TableCell>
        <TableCell>{formatValue(row.key, row.value)}</TableCell>
      </TableRow>
    )),
    ...renderUserRow("Admin Users", "adminUsers", appInfo.adminUsers),
    ...renderUserRow("Regular Users", "regularUsers", appInfo.regularUsers),
  ].filter(Boolean); // Remove any null entries

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Application Information
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Property</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{allRows}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
